/**
 * Authentication commands: login, logout, whoami
 *
 * Authentication is by SPACECODE_TOKEN (PAT or shared secret); see login().
 */

import { loadAuth, saveAuth, clearAuth as clearAuthFile, loadConfig } from '../config.js';
import { DEFAULT_API_URL } from '@spacecode/shared/remote';
import { BackendApiClient, AuthError } from '../api/client.js';
import chalk from 'chalk';
import { deviceLogin } from '../auth/device-login.js';

// SaaS Platform's SpaceCode host, which serves the device-code login endpoints and approval page.
const DEFAULT_AUTH_URL = 'https://app.spacecode.app';

/**
 * Sign in with an SP API key (`spacecode login --api-key sk_...`; create one in
 * the SpaceCode dashboard on SaaS Platform). The key is stored in ~/.spacecode/auth.json
 * (mode 0600) and sent as the Bearer token; the server verifies it on every request.
 *
 * Without a key, runs SP's device-code flow: print a code, open the browser to approve,
 * and save the minted key. The GitHub OAuth redirect flow was removed (ADR-2);
 * personal access tokens and the shared secret remain for service use.
 */
export async function login(options: { apiKey?: string } = {}): Promise<void> {
  const apiKey = options.apiKey?.trim();
  if (apiKey) {
    if (!apiKey.startsWith('sk_') || apiKey.length < 12 || /\s/.test(apiKey)) {
      throw new Error('That does not look like a SpaceCode API key (expected sk_...).');
    }
    await saveApiKey(apiKey);
    console.log(chalk.green('API key saved. You are signed in.'));
    return;
  }

  // No key given: browser sign-in via SP's device-code flow (approve in the browser, key is saved here).
  const { key } = await deviceLogin(process.env.SPACECODE_AUTH_URL ?? DEFAULT_AUTH_URL, {
    post: postJson,
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    now: () => Date.now(),
    openBrowser: async (url) => {
      const { default: open } = await import('open');
      await open(url);
    },
    log: (msg) => console.log(msg),
  });
  await saveApiKey(key);
  console.log(chalk.green('Signed in. API key saved to ~/.spacecode/auth.json.'));
}

async function saveApiKey(apiKey: string): Promise<void> {
  await saveAuth({
    accessToken: apiKey,
    user: { id: 'api-key', githubId: '', githubLogin: `api-key ${apiKey.slice(0, 7)}...` },
  });
}

async function postJson(url: string, body: unknown): Promise<{ status: number; body: unknown }> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(15_000),
  });
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    // Non-JSON body (e.g. a proxy error page): the status alone drives the caller's error.
  }
  return { status: res.status, body: parsed };
}

/**
 * Clear authentication credentials
 */
export async function logout(): Promise<void> {
  const auth = await loadAuth();
  await clearAuthFile();

  if (!auth.accessToken) {
    console.log(chalk.yellow('Already logged out'));
    return;
  }

  const username = auth.user?.githubLogin ?? 'unknown';
  console.log(chalk.green(`Logged out (${username})`));
}

/**
 * Show current user information
 */
export async function whoami(): Promise<void> {
  const auth = await loadAuth();

  if (!auth.accessToken) {
    console.log(chalk.yellow('Not logged in. Run `spacecode login` or any command to authenticate.'));
    return;
  }

  if (!auth.user) {
    console.log(chalk.yellow('Logged in (no user info cached)'));
    return;
  }

  const config = await loadConfig();
  const apiUrl = process.env.SPACECODE_API_URL ?? config.apiUrl ?? DEFAULT_API_URL;
  const client = new BackendApiClient(apiUrl, auth.accessToken);

  try {
    const user = await client.getCurrentUser();
    console.log(chalk.cyan(`${chalk.bold(user.githubLogin)}`));
    console.log(chalk.gray(`  User ID: ${user.id}`));
    console.log(chalk.gray(`  GitHub ID: ${user.githubId}`));
  } catch (error) {
    if (error instanceof AuthError) {
      console.log(chalk.yellow(`${auth.user.githubLogin} (session expired)`));
      console.log(chalk.gray('Run `spacecode login` to re-authenticate'));
    } else {
      console.log(chalk.cyan(`${chalk.bold(auth.user.githubLogin)}`));
      console.log(chalk.gray(`  User ID: ${auth.user.id}`));
      console.log(chalk.gray(`  (backend unavailable — showing cached info)`));
    }
  }
}

/**
 * Ensure user is authenticated. If no valid JWT exists, auto-opens
 * the GitHub OAuth flow in the browser.
 */
export async function requireAuth(): Promise<BackendApiClient> {
  const auth = await loadAuth();
  const config = await loadConfig();
  const apiUrl = process.env.SPACECODE_API_URL ?? config.apiUrl ?? DEFAULT_API_URL;

  // If we have a token, verify it's still valid
  if (auth.accessToken) {
    const client = new BackendApiClient(apiUrl, auth.accessToken);
    try {
      await client.getCurrentUser();
      return client;
    } catch (error) {
      if (!(error instanceof AuthError)) throw error;
      // Token expired — fall through to re-auth
      console.log(chalk.yellow('Session expired. Re-authenticating...'));
    }
  }

  // CI/env-var override: allow SPACECODE_TOKEN for headless environments
  const envToken = process.env.SPACECODE_TOKEN;
  if (envToken) {
    return new BackendApiClient(apiUrl, envToken);
  }

  // No valid token — auto-open OAuth
  await login();
  const freshAuth = await loadAuth();
  if (!freshAuth.accessToken) {
    throw new Error('Authentication failed — no token received');
  }
  return new BackendApiClient(apiUrl, freshAuth.accessToken);
}
